import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { OwnerType, PointOfView } from '../../../types/__generated__';
import { ShareContextProvider } from '../../context/ShareContext';
import { usePageOwner, useShareUrls } from '../../hooks';
import { useProduct } from '../../hooks/page/product/useProduct';
import { useCharity } from '../../hooks/page/useCharity';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { generateEmailBody } from '../../utils/generateEmailBody';

interface OnepageShareProps {
  children: ReactNode;
}

export function OnePageShareProvider({ children }: OnepageShareProps) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { isPageOwner } = usePageOwner();
  const { data: basePageData } = useBasePageQuery();
  const { name: charityName, isNonUKCharity } = useCharity();
  const {
    data: { features, allowedPointsOfView, ownerType },
  } = useProduct();

  const selectPointOfView = (
    firstPersonTranslation: string,
    thirdPersonTranslation: string,
  ) => {
    if (allowedPointsOfView?.includes(PointOfView.FirstPerson)) {
      return isPageOwner ? firstPersonTranslation : thirdPersonTranslation;
    } else {
      return thirdPersonTranslation;
    }
  };

  const getSharingTranslation = (
    individualFirstPerson: string,
    individualThirdPerson: string,
    organisationFirstPerson: string,
    organisationThirdPerson: string,
    fallback: string,
    overrideFirstPerson?: string | null,
    overrideThirdPerson?: string | null,
    url?: string,
  ) => {
    const args = {
      name: basePageData?.page?.owner?.name,
      charityName,
      pageUrl: url,
    };

    if (overrideFirstPerson && !overrideThirdPerson) {
      return t(selectPointOfView(overrideFirstPerson, fallback), args);
    }

    if (!overrideFirstPerson && overrideThirdPerson) {
      return t(overrideThirdPerson, args);
    }

    if (overrideFirstPerson && overrideThirdPerson)
      return t(
        selectPointOfView(overrideFirstPerson, overrideThirdPerson),
        args,
      );

    switch (ownerType) {
      case OwnerType.Individual:
        return t(
          selectPointOfView(individualFirstPerson, individualThirdPerson),
          args,
        );
      case OwnerType.Organisation:
        return t(
          selectPointOfView(organisationFirstPerson, organisationThirdPerson),
          args,
        );
      default:
        return t(fallback);
    }
  };

  const twitterMapper = () =>
    getSharingTranslation(
      'onepage.share.twitter.individual.firstPerson',
      'onepage.share.twitter.individual.thirdPerson',
      'onepage.share.twitter.organisation.firstPerson',
      'onepage.share.twitter.organisation.thirdPerson',
      'onepage.share.twitter.fallback',
      features?.sharing?.twitterKeyFirstPerson,
      features?.sharing?.twitterKeyThirdPerson,
    );

  const whatsAppAndSmsMapper = (url: string) =>
    getSharingTranslation(
      'onepage.share.whatsAppAndSms.individual.firstPerson',
      'onepage.share.whatsAppAndSms.individual.thirdPerson',
      'onepage.share.whatsAppAndSms.organisation.firstPerson',
      'onepage.share.whatsAppAndSms.organisation.thirdPerson',
      'onepage.share.whatsAppAndSms.fallback',
      features?.sharing?.whatsAppAndSmsKeyFirstPerson,
      features?.sharing?.whatsAppAndSmsKeyThirdPerson,
      url,
    );

  const emailShareMessage = getSharingTranslation(
    'onepage.share.emailBody.individual.firstPerson',
    'onepage.share.emailBody.individual.thirdPerson',
    'onepage.share.emailBody.organisation.firstPerson',
    'onepage.share.emailBody.organisation.thirdPerson',
    'onepage.share.emailBody.fallback',
    features?.sharing?.emailBodyKeyFirstPerson,
    features?.sharing?.emailBodyKeyThirdPerson,
  );

  const giftAidMessage = isNonUKCharity
    ? ''
    : t('common.charityGiftAid', {
        charityName,
      });

  const emailMapper = (url: string) => ({
    subject: t('common.defaultEmailSubject'),
    body: generateEmailBody({
      greeting: t('common.greeting'),
      message: emailShareMessage,
      shareUrl: url,
      giftAidMessage,
      thanks: t('common.emailThanks'),
    }),
  });

  const shareItems = useShareUrls({
    email: emailMapper,
    sms: whatsAppAndSmsMapper,
    twitter: twitterMapper,
    whatsApp: whatsAppAndSmsMapper,
  });

  return (
    <ShareContextProvider value={{ shareItems }}>
      {children}
    </ShareContextProvider>
  );
}
